import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import Logo, { TocoaScript } from '../components/Logo';
import { cn } from '@/lib/utils';

const PhoneFrame = ({ children, className = '', tagline = 'Tu ciudad, en tus manos', subtitle = 'Comida  •  Mandaditos 7  •  Envíos  •  Todo en Tocoa', scriptSub = 'Siempre más cerca de ti', extraLogo }) => {
  const { pathname } = useLocation();
  return (
    <div className={cn('h-[100dvh] md:h-auto md:min-h-0 overflow-hidden md:overflow-visible bg-[#F7F7F9] md:py-6 md:px-4 flex flex-col items-center', className)}>
      <header className="hidden md:flex w-full max-w-5xl items-center justify-between mb-5 px-2">
        <div className="flex items-center gap-5">
          <Logo size="md" />
          {extraLogo}
          <div className="border-l border-gray-300 pl-5">
            <p className="font-extrabold text-xl text-[#1E2130]">{tagline}</p>
            <p className="text-sm text-gray-600 font-medium">{subtitle}</p>
          </div>
        </div>
        <div className="text-right">
          <TocoaScript className="text-4xl leading-none" />
          <p className="font-script text-lg text-[#1E2130] -mt-1">{scriptSub}</p>
        </div>
      </header>
      <div className="relative w-full md:w-[390px] h-full md:h-[min(820px,calc(100vh-120px))] md:min-h-[600px] md:rounded-[46px] bg-[#111] md:p-[9px] md:phone-shell shrink-0">
        <div className="relative w-full h-full bg-white md:rounded-[38px] overflow-hidden flex flex-col" style={{ paddingTop: 'var(--safe-top)' }}>
          <div className="flex-1 flex flex-col min-h-0 fade-up" key={pathname}>{children}</div>
        </div>
      </div>
    </div>
  );
};

// ---- Mobile UI primitives ----
export const Screen = ({ children, className = '', nav = false }) => (
  <div className={cn('flex-1 overflow-y-auto no-scrollbar pt-2', nav ? 'pb-24' : 'pb-6', className)}>{children}</div>
);

export const TopBar = ({ title, onBack, right, className = '', transparent = false }) => {
  const navigate = useNavigate();
  return (
    <div className={cn('flex items-center gap-2 px-4 h-12', transparent ? '' : 'bg-white', className)}>
      <button data-testid="back-btn" onClick={onBack || (() => navigate(-1))} className="w-9 h-9 -ml-2 rounded-full flex items-center justify-center hover:bg-gray-100 active:scale-95 transition-transform"><ChevronLeft size={24} /></button>
      <h1 className="flex-1 text-center font-bold text-[17px] text-[#1E2130] pr-7">{title}</h1>
      {right && <div className="absolute right-4">{right}</div>}
    </div>
  );
};

export const PrimaryButton = ({ children, className = '', variant = 'red', ...rest }) => {
  const v = {
    red: 'bg-[#EE3B36] text-white hover:bg-[#D93129] shadow-lg shadow-red-500/25',
    dark: 'bg-[#1E2130] text-white hover:bg-black',
    green: 'bg-[#22A45D] text-white hover:bg-[#1B8A4D] shadow-lg shadow-emerald-500/25',
    outline: 'bg-white text-[#EE3B36] border-2 border-[#EE3B36] hover:bg-[#FDECEC]',
    outlineDark: 'bg-transparent text-white border-2 border-white/80 hover:bg-white/10',
    light: 'bg-[#FDECEC] text-[#EE3B36] hover:bg-[#F9D4D2]',
  }[variant];
  return (
    <button className={cn('w-full h-12 rounded-xl font-semibold text-[15px] active:scale-[0.98] transition-[transform,background-color] disabled:opacity-50 disabled:pointer-events-none', v, className)} {...rest}>{children}</button>
  );
};

export const MobileInput = ({ icon: Icon, label, right, className = '', ...rest }) => (
  <label className={cn('flex items-center gap-3 h-14 px-4 rounded-xl border border-gray-200 bg-white focus-within:border-[#EE3B36] focus-within:ring-2 focus-within:ring-[#EE3B36]/15', className)}>
    {Icon && <Icon size={20} className="text-gray-400 shrink-0" />}
    <div className="flex-1 min-w-0">
      {label && <p className="text-[11px] text-gray-500 leading-none mb-1">{label}</p>}
      <input className="w-full bg-transparent outline-none text-[15px] text-[#1E2130] placeholder:text-gray-400 leading-none" {...rest} />
    </div>
    {right}
  </label>
);

export const SectionTitle = ({ title, action, onAction }) => (
  <div className="flex items-center justify-between px-4 mb-3">
    <h3 className="font-extrabold text-[17px] tracking-tight text-[#1E2130]">{title}</h3>
    {action && <button onClick={onAction} className="text-[13px] font-bold text-[#EE3B36] press">{action}</button>}
  </div>
);

export const MockMap = ({ className = '', children }) => (
  <div className={cn('relative mock-map overflow-hidden', className)}>
    {/* fake roads */}
    <div className="absolute left-0 right-0 top-1/3 h-3 bg-white/90" />
    <div className="absolute left-0 right-0 top-2/3 h-2 bg-white/80" />
    <div className="absolute top-0 bottom-0 left-1/4 w-3 bg-white/90" />
    <div className="absolute top-0 bottom-0 left-2/3 w-2 bg-white/80" />
    <div className="absolute w-24 h-16 bg-[#d9ead3] rounded-xl left-6 top-6 opacity-80" />
    <div className="absolute w-20 h-20 bg-[#cfe2f3] rounded-full right-8 bottom-6 opacity-70" />
    {children}
  </div>
);

export default PhoneFrame;
